import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";
import { StatusBadge } from "../shared/StatusBadge";
import { formatDate } from "../shared/dateUtils";

const C = {
  navy: colors.darkBlue,
  red: colors.red,
  green: colors.green,
  slate: colors.slate,
  border: colors.border,
  bg: colors.lightGrey,
  white: colors.white,
  text: colors.text,
};

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtType(type) {
  return (type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function PhotoThumb({ url, onClick }) {
  return (
    <img
      src={url}
      alt="defect"
      onClick={() => onClick(url)}
      style={{
        width: 80,
        height: 60,
        objectFit: "cover",
        borderRadius: 6,
        border: `1px solid ${C.border}`,
        cursor: "pointer",
        flexShrink: 0,
      }}
    />
  );
}

function Lightbox({ url, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
    >
      <img
        src={url}
        alt="defect full"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, objectFit: "contain" }}
      />
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 20,
          right: 24,
          background: "none",
          border: "none",
          color: "#fff",
          fontSize: 28,
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}

// A defect can sit on more than one quote (RectificationQuoteItem.inspection_item
// is a plain FK), so this takes the furthest-along quote rather than whichever
// happened to be read last. A REJECTED quote falls back to "reported" — the
// defect is still outstanding and still needs dealing with.
const QUOTE_RANK = { rejected: 0, draft: 1, sent: 1, approved: 2 };

function statusForDefect(quoteStatuses) {
  let best = -1;
  (quoteStatuses || []).forEach((st) => {
    const rank = QUOTE_RANK[st] ?? 0;
    if (rank > best) best = rank;
  });
  if (best >= 2) return "quote_approved";
  if (best >= 1) return "quoted";
  return "reported";
}

function DefectCard({ defect, onPhotoClick, onRectify }) {
  const photos = [defect.photo_1_url, defect.photo_2_url, defect.photo_3_url].filter(Boolean);
  const assetLine = [defect.asset_type_name, defect.floor_level, defect.location].filter(Boolean).join(" · ");
  // The date lives in the group header now, so it is not repeated per row.
  const metaLine = [defect.category_name, fmtType(defect.inspection_type)].filter(Boolean).join(" · ");

  return (
    <div
      style={{
        borderLeft: `4px solid ${C.red}`,
        backgroundColor: "#fff8f8",
        borderRadius: "0 8px 8px 0",
        padding: "12px 16px",
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: C.navy, fontSize: 14, marginBottom: 2 }}>{assetLine || "Asset"}</div>
          <div style={{ fontSize: 12, color: C.slate, marginBottom: 6 }}>{metaLine}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <StatusBadge status={defect.status} />
          <button
            onClick={() => onRectify(defect)}
            title="Mark this defect as rectified and remove it from the register"
            style={{
              background: "none",
              border: `1px solid ${C.green}`,
              color: C.green,
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Mark Rectified
          </button>
        </div>
      </div>
      {defect.notes && (
        <div style={{ fontSize: 13, color: C.text, marginBottom: photos.length ? 10 : 0 }}>{defect.notes}</div>
      )}
      {photos.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {photos.map((url, i) => (
            <PhotoThumb key={i} url={url} onClick={onPhotoClick} />
          ))}
        </div>
      )}
    </div>
  );
}

function VisitCard({ group, expanded, onToggle, onPhotoClick, onRectify }) {
  const count = group.defects.length;
  return (
    <div style={{ ...styles.card, marginBottom: 12 }}>
      <div
        onClick={onToggle}
        style={{
          padding: "14px 20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: expanded ? "#f8fafc" : C.white,
          borderBottom: expanded ? `1px solid ${C.border}` : "none",
          borderRadius: expanded ? "12px 12px 0 0" : 12,
          transition: "background 0.15s",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>
            {group.site_name}
            <span style={{ color: C.slate, fontWeight: 500 }}> — {fmtDate(group.date)}</span>
          </div>
          {group.site_address && (
            <div style={{ fontSize: 12, color: C.slate, marginTop: 2 }}>{group.site_address}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              backgroundColor: "#fee2e2",
              color: C.red,
              fontWeight: 700,
              fontSize: 13,
              padding: "3px 12px",
              borderRadius: 20,
            }}
          >
            {count} {count === 1 ? "defect" : "defects"}
          </span>
          <span style={{ color: C.slate, fontSize: 18, lineHeight: 1 }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: "12px 16px" }}>
          {group.defects.map((d) => (
            <DefectCard key={d.id} defect={d} onPhotoClick={onPhotoClick} onRectify={onRectify} />
          ))}
        </div>
      )}
    </div>
  );
}

export function DefectsRegisterPage() {
  const [loading, setLoading] = useState(true);
  const [inspections, setInspections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [rectifyTarget, setRectifyTarget] = useState(null);
  const [rectifyNotes, setRectifyNotes] = useState("");
  const [rectifySaving, setRectifySaving] = useState(false);
  const [rectifyError, setRectifyError] = useState("");

  const loadData = () => {
    // Each call is guarded separately: rectification-quotes is management-only
    // (IsAdminOrManagement) while the other two are open to field techs, so an
    // unguarded Promise.all would blank the whole page for a tech.
    Promise.all([
      // include_locked: a defect must stay on the register even once its
      // inspection report has been locked in Records.
      axios.get(`${API}/inspections/?include_locked=1`).catch(() => ({ data: [] })),
      axios.get(`${API}/rectification-requests/`).catch(() => ({ data: [] })),
      axios.get(`${API}/rectification-quotes/`).catch(() => ({ data: [] })),
    ])
      .then(([inspRes, reqRes, quoteRes]) => {
        setInspections(inspRes.data || []);
        setRequests(reqRes.data || []);
        setQuotes(quoteRes.data || []);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadData, []);

  const groups = useMemo(() => {
    // Inspection items a tech has already attested as rectified. This is the
    // automatic way out of the register; the manual `rectified` flag on the
    // item is the other.
    const attestedIds = new Set();
    requests.forEach((req) => {
      (req.items || []).forEach((item) => {
        if (item.attested) {
          const inspItemId = item.quote_item_detail?.inspection_item;
          if (inspItemId) attestedIds.add(inspItemId);
        }
      });
    });

    // inspection_item id -> every quote status it appears on
    const quoteStatuses = {};
    quotes.forEach((q) => {
      (q.items || []).forEach((qi) => {
        if (!qi.inspection_item) return;
        if (!quoteStatuses[qi.inspection_item]) quoteStatuses[qi.inspection_item] = [];
        quoteStatuses[qi.inspection_item].push(q.status);
      });
    });

    const open = [];
    inspections.forEach((insp) => {
      (insp.items || [])
        .filter((item) => item.result === "fail" && !item.swapped)
        .forEach((item) => {
          // Rectified defects leave the register entirely — the site records
          // hold the history, so no completed list is kept.
          if (item.rectified || attestedIds.has(item.id)) return;
          open.push({
            id: item.id,
            site_id: insp.site,
            site_name: insp.site_name,
            site_address: insp.site_address,
            inspection_date: insp.date,
            inspection_type: insp.inspection_type,
            asset_type_name: item.asset_type_name,
            category_name: item.category_name,
            floor_level: item.floor_level,
            location: item.location,
            notes: item.notes,
            photo_1_url: item.photo_1_url,
            photo_2_url: item.photo_2_url,
            photo_3_url: item.photo_3_url,
            status: statusForDefect(quoteStatuses[item.id]),
          });
        });
    });

    // One row per site + visit date. Post-split a site can have several section
    // inspections on the same day; those belong together on one row.
    const map = {};
    open.forEach((item) => {
      // The local day of the visit. Slicing the datetime string would key
      // off the UTC day and split a morning visit across two rows.
      const dayKey = item.inspection_date
        ? formatDate(new Date(item.inspection_date))
        : "";
      const key = `${item.site_id}|${dayKey}`;
      if (!map[key]) {
        map[key] = {
          key,
          site_id: item.site_id,
          site_name: item.site_name,
          site_address: item.site_address,
          date: item.inspection_date,
          defects: [],
        };
      }
      map[key].defects.push(item);
    });

    return Object.values(map)
      .map((g) => ({
        ...g,
        defects: g.defects.sort((a, b) =>
          (a.asset_type_name || "").localeCompare(b.asset_type_name || ""),
        ),
      }))
      .sort(
        (a, b) =>
          new Date(b.date) - new Date(a.date) ||
          (a.site_name || "").localeCompare(b.site_name || ""),
      );
  }, [inspections, requests, quotes]);

  const total = groups.reduce((s, g) => s + g.defects.length, 0);

  function toggleGroup(key) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const openRectify = (defect) => {
    setRectifyTarget(defect);
    setRectifyNotes("");
    setRectifyError("");
  };

  const submitRectify = async () => {
    if (!rectifyTarget) return;
    setRectifySaving(true);
    setRectifyError("");
    try {
      // A dedicated action, not a PATCH: the inspection may be locked in
      // Records, and clearing a defect must not require unlocking the report.
      await axios.post(`${API}/inspection-items/${rectifyTarget.id}/mark-rectified/`, {
        rectified: true,
        notes: rectifyNotes,
      });
      setRectifyTarget(null);
      loadData();
    } catch (e) {
      setRectifyError("Could not mark this defect as rectified. Please try again.");
    }
    setRectifySaving(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Defect Register</div>
        {!loading && total > 0 && (
          <span
            style={{
              backgroundColor: "#fee2e2",
              color: C.red,
              fontSize: 12,
              fontWeight: 700,
              padding: "3px 12px",
              borderRadius: 20,
            }}
          >
            {total} open {total === 1 ? "defect" : "defects"}
          </span>
        )}
      </div>

      <div style={{ ...styles.content, overflowY: "auto" }}>
        {loading ? (
          <div style={styles.loading}>Loading defects...</div>
        ) : groups.length === 0 ? (
          <div style={{ padding: "60px 40px", textAlign: "center", color: C.slate, fontSize: 15 }}>
            No open defects — every reported defect has been rectified.
          </div>
        ) : (
          groups.map((group) => (
            <VisitCard
              key={group.key}
              group={group}
              expanded={expandedKeys.has(group.key)}
              onToggle={() => toggleGroup(group.key)}
              onPhotoClick={(url) => setLightboxUrl(url)}
              onRectify={openRectify}
            />
          ))
        )}
      </div>

      {rectifyTarget && (
        <SimpleModal
          title="Mark Defect Rectified"
          onClose={() => setRectifyTarget(null)}
          onSave={submitRectify}
          saving={rectifySaving}
          error={rectifyError}
          saveLabel="Mark Rectified"
        >
          <div style={{ fontSize: 13, color: C.text, marginBottom: 4, fontWeight: 600 }}>
            {[rectifyTarget.asset_type_name, rectifyTarget.floor_level, rectifyTarget.location]
              .filter(Boolean)
              .join(" · ")}
          </div>
          <div style={{ fontSize: 12, color: C.slate, marginBottom: 14 }}>
            {rectifyTarget.site_name} · {fmtDate(rectifyTarget.inspection_date)}
          </div>
          <label style={labelStyle}>What was done?</label>
          <textarea
            autoFocus
            value={rectifyNotes}
            onChange={(e) => setRectifyNotes(e.target.value)}
            placeholder="e.g. Replaced faulty exit light and retested"
            style={{ ...inputStyle, height: 90, resize: "vertical" }}
          />
          <div style={{ fontSize: 11, color: C.slate, marginTop: 8 }}>
            This removes the defect from the register. Your name and the date are recorded with it.
          </div>
        </SimpleModal>
      )}

      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
